import { GroupIcon } from '@blocksuite/icons/rc';
import { type FC, type ReactNode } from 'react';

import {
  SettingsEntityAvatar,
  SettingsEntitySummary,
  SettingsExpandableItem,
  SettingsMetaItem,
} from './settings-entity';
import * as styles from './settings-identity.css';

export interface SettingsPersonIdentity {
  id?: string;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
}

export interface SettingsGroupIdentity {
  id?: string;
  name: string;
  description?: string | null;
}

interface SettingsIdentityBaseProps {
  badges?: ReactNode;
  meta?: ReactNode;
  aside?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  dataTestId?: string;
  variant?: 'normal' | 'compact';
}

export interface SettingsPersonSummaryProps extends SettingsIdentityBaseProps {
  person: SettingsPersonIdentity;
  fallbackName?: string;
  showEmailMeta?: boolean;
}

export interface SettingsGroupSummaryProps extends SettingsIdentityBaseProps {
  group: SettingsGroupIdentity;
}

export interface SettingsPersonExpandableItemProps extends SettingsPersonSummaryProps {
  children?: ReactNode;
}

export interface SettingsGroupExpandableItemProps extends SettingsGroupSummaryProps {
  children?: ReactNode;
}

function personTitle(person: SettingsPersonIdentity, fallbackName: string) {
  return person.name || person.email?.split('@')[0] || fallbackName;
}

export const SettingsPersonSummary: FC<SettingsPersonSummaryProps> = ({
  person,
  fallbackName = 'User',
  showEmailMeta = true,
  badges,
  meta,
  aside,
  expanded,
  onToggle,
  dataTestId,
  variant = 'normal',
}) => {
  const title = personTitle(person, fallbackName);

  return (
    <SettingsEntitySummary
      expanded={expanded}
      onToggle={onToggle}
      dataTestId={dataTestId}
      leading={
        <SettingsEntityAvatar
          size={variant === 'compact' ? 24 : 28}
          userId={person.id}
          name={title}
          url={person.avatarUrl}
          fallbackName={fallbackName}
        />
      }
      title={title}
      subtitle={showEmailMeta && person.email ? person.email : undefined}
      badges={badges}
      meta={meta}
      aside={aside}
    />
  );
};

export const SettingsGroupSummary: FC<SettingsGroupSummaryProps> = ({
  group,
  badges,
  meta,
  aside,
  expanded,
  onToggle,
  dataTestId,
  variant = 'normal',
}) => {
  return (
    <SettingsEntitySummary
      expanded={expanded}
      onToggle={onToggle}
      dataTestId={dataTestId}
      leading={
        <GroupIcon
          className={
            variant === 'compact' ? styles.compactGroupIcon : styles.groupIcon
          }
        />
      }
      title={group.name}
      badges={badges}
      meta={
        meta ??
        (group.description ? (
          <SettingsMetaItem>{group.description}</SettingsMetaItem>
        ) : undefined)
      }
      aside={aside}
    />
  );
};

export const SettingsPersonExpandableItem: FC<
  SettingsPersonExpandableItemProps
> = ({ children, ...summaryProps }) => {
  const title = personTitle(
    summaryProps.person,
    summaryProps.fallbackName ?? 'User'
  );

  return (
    <SettingsExpandableItem
      expanded={summaryProps.expanded}
      onToggle={summaryProps.onToggle}
      dataTestId={summaryProps.dataTestId}
      leading={
        <SettingsEntityAvatar
          size={summaryProps.variant === 'compact' ? 24 : 28}
          userId={summaryProps.person.id}
          name={title}
          url={summaryProps.person.avatarUrl}
          fallbackName={summaryProps.fallbackName ?? 'User'}
        />
      }
      title={title}
      subtitle={
        (summaryProps.showEmailMeta ?? true) && summaryProps.person.email
          ? summaryProps.person.email
          : undefined
      }
      badges={summaryProps.badges}
      meta={summaryProps.meta}
      aside={summaryProps.aside}
    >
      {children}
    </SettingsExpandableItem>
  );
};

export const SettingsGroupExpandableItem: FC<
  SettingsGroupExpandableItemProps
> = ({ children, ...summaryProps }) => {
  return (
    <SettingsExpandableItem
      expanded={summaryProps.expanded}
      onToggle={summaryProps.onToggle}
      dataTestId={summaryProps.dataTestId}
      leading={
        <GroupIcon
          className={
            summaryProps.variant === 'compact'
              ? styles.compactGroupIcon
              : styles.groupIcon
          }
        />
      }
      title={summaryProps.group.name}
      badges={summaryProps.badges}
      meta={
        summaryProps.meta ??
        (summaryProps.group.description ? (
          <SettingsMetaItem>{summaryProps.group.description}</SettingsMetaItem>
        ) : undefined)
      }
      aside={summaryProps.aside}
    >
      {children}
    </SettingsExpandableItem>
  );
};
